import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { services, siteConfig } from "../data/mockData";
import {
  Shield, HeartHandshake, Smile, Eye, Building, Plus, Plane,
  Check, Phone, Calendar,
} from "lucide-react";

const iconMap = {
  Shield, HeartHandshake, Smile, Eye, Building, Plus, Plane,
};

const ServiceDetailRow = ({ service, isReversed }) => {
  const IconComponent = iconMap[service.icon];
  const isLifeInsurance = service.title === "Life Insurance";
  const isVisionCoverage = service.title === "Vision Coverage";
  const isDentalCoverage = service.title === "Dental Coverage";
  const isTravelInsurance = service.title === "Travel Insurance";
  
  const getButtonConfig = () => {
    if (isLifeInsurance) {
      return {
        url: siteConfig.lifeInsuranceQuoteUrl,
        text: "Get Instant Life Insurance Quote",
        external: true
      };
    }
    if (isVisionCoverage) {
      return {
        url: siteConfig.visionQuoteUrl,
        text: "Get Instant Vision Quote",
        external: true
      };
    }
    if (isDentalCoverage) {
      return {
        url: siteConfig.dentalQuoteUrl,
        text: "Get Instant Dental Quote",
        external: true
      };
    }
    if (isTravelInsurance) {
      return {
        url: siteConfig.travelQuoteUrl,
        text: "Get Instant Travel Insurance Quote",
        external: true
      };
    }
    return {
      url: siteConfig.calendlyUrl,
      text: "Get a Quote",
      external: false
    };
  };
  
  const buttonConfig = getButtonConfig();
  
  return (
    <div
      className={`grid md:grid-cols-2 gap-12 items-center ${
        isReversed ? "md:flex-row-reverse" : ""
      }`}
    >
      <div className={isReversed ? "md:order-2" : ""}>
        <div className="w-14 h-14 bg-[#E8F5E9] rounded-xl flex items-center justify-center mb-5">
          {IconComponent && <IconComponent size={28} className="text-[#3A7D44]" />}
        </div>
        <h2 className="text-2xl font-bold text-[#1B2A1B] mb-4">{service.title}</h2>
        <p className="text-[#5A6B5A] leading-relaxed mb-6">{service.description}</p>
        <ul className="space-y-3 mb-6">
          {service.details.map((detail) => (
            <li key={detail} className="flex items-start gap-3">
              <Check size={16} className="text-[#3A7D44] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-[#5A6B5A]">{detail}</span>
            </li>
          ))}
        </ul>
        <a
          href={buttonConfig.url}
          target={buttonConfig.external ? "_blank" : "_self"}
          rel={buttonConfig.external ? "noopener noreferrer" : ""}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#3A7D44] rounded-lg hover:bg-[#2D5A35] transition-colors"
        >
          {buttonConfig.text}
        </a>
      </div>
      <div
        className={`bg-[#F1F8F2] rounded-2xl p-12 flex items-center justify-center ${
          isReversed ? "md:order-1" : ""
        }`}
      >
        <div className="w-32 h-32 bg-[#3A7D44]/10 rounded-3xl flex items-center justify-center">
          {IconComponent && <IconComponent size={64} className="text-[#3A7D44]" />}
        </div>
      </div>
    </div>
  );
};

const ServicesCTA = () => (
  <section className="py-20 bg-[#1B2A1B]">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        Not Sure What You Need?
      </h2>
      <p className="text-gray-400 mb-8 leading-relaxed">
        That's exactly what I'm here for. Let's talk about your situation
        and figure out the best path forward together.
      </p>
      <div className="flex flex-wrap justify-center gap-4">
        <a
          href={siteConfig.calendlyUrl}
          
          
          className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-[#1B2A1B] bg-white rounded-lg hover:bg-gray-100 transition-colors"
        >
          <Calendar size={16} />
          Book a Free Consultation
        </a>
        <a
          href={`tel:${siteConfig.phone}`}
          className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors"
        >
          <Phone size={16} />
          {siteConfig.phoneFormatted}
        </a>
      </div>
    </div>
  </section>
);

const ServicesPage = () => {
  return (
    <>
      <Helmet>
        <title>Individual, Family &amp; Small Business Health Insurance | Hess Insurance Solutions</title>
        <meta
          name="description"
          content="Compare health insurance for individuals, families, self-employed professionals, and small businesses. Explore major medical, GigCare, private, and supplemental coverage with help from a licensed broker."
        />
        <meta
          property="og:title"
          content="Individual, Family & Small Business Health Insurance | Hess Insurance Solutions"
        />
        <meta
          property="og:description"
          content="Compare health insurance for individuals, families, self-employed professionals, and small businesses. Explore major medical, GigCare, private, and supplemental coverage with help from a licensed broker."
        />
        <link rel="canonical" href="https://hessinsurancesolutions.com/services" />
      </Helmet>
      <section className="pt-28 pb-16 bg-gradient-to-b from-[#F1F8F2] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-sm font-semibold text-[#3A7D44] uppercase tracking-wider">
            What I Offer
          </span>
          <h1
            className="text-4xl sm:text-5xl font-bold text-[#1B2A1B] mt-3 mb-6"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Insurance Services
          </h1>
          <p className="text-lg text-[#5A6B5A] leading-relaxed max-w-2xl mx-auto">
            From health and life insurance to dental, vision, and supplemental
            coverage, I help you build a complete protection package tailored to your needs.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <ServiceDetailRow
                key={service.id}
                service={service}
                isReversed={index % 2 !== 0}
              />
            ))}
          </div>
        </div>
      </section>

      <ServicesCTA />
    </>
  );
};

export default ServicesPage;
